import { useChat } from '@ai-sdk/react';
import { getCommanderClientHeaders } from '@commander/api-client';
import { useAuth } from '@commander/auth';
import { analytics } from '@commander/product-analytics';
import type { UIMessage } from 'ai';
import { DefaultChatTransport } from 'ai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { COMMANDER_GO_API_PATH, COMMANDER_GO_THREADS_PATH } from '../constants.js';

interface ThreadSummary {
  id: string;
  title: string | null;
  app_context: string | null;
  pinned_at: string | null;
  created_at: string;
  updated_at: string;
}

interface StoredMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  sequence_number: number;
  metadata: any;
  created_at: string;
}

/** Convert stored messages to the UIMessage format expected by useChat. */
function toUIMessages(stored: StoredMessage[]): UIMessage[] {
  return stored.map(
    (msg) =>
      ({
        id: msg.id,
        role: msg.role as 'user' | 'assistant',
        parts: msg.metadata?.parts ?? [{ type: 'text' as const, text: msg.content ?? '' }],
        createdAt: new Date(msg.created_at),
      }) as UIMessage & { createdAt: Date }
  );
}

export function useCommanderGo(appContext?: 'crm' | 'prospecting') {
  const { token, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [threadId, setThreadId] = useState<string>(() => crypto.randomUUID());
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [showThreadList, setShowThreadList] = useState(false);
  const [threadsLoaded, setThreadsLoaded] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [previousCursor, setPreviousCursor] = useState<number | null>(null);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  // Messages to restore after threadId change triggers useChat reinitialization
  const pendingMessagesRef = useRef<UIMessage[] | null>(null);
  const hasPolledTitleRef = useRef<Set<string>>(new Set());

  // Analytics tracking refs
  const sendStartMsRef = useRef<number | null>(null);
  const isRetryRef = useRef(false);
  const prevIsOpenRef = useRef(isOpen);

  // Reset all state when user identity changes (logout → login as different user)
  const userIdRef = useRef(user?.id);
  useEffect(() => {
    if (user?.id === userIdRef.current) return;
    userIdRef.current = user?.id;
    pendingMessagesRef.current = [];
    setThreadId(crypto.randomUUID());
    setThreads([]);
    setThreadsLoaded(false);
    setShowThreadList(false);
    setNextCursor(null);
    setPreviousCursor(null);
    hasPolledTitleRef.current = new Set();
  }, [user?.id]);

  // Resolved per request so the idle signal reflects the moment of sending.
  const baseHeaders = useCallback(
    (): Record<string, string> => ({
      ...getCommanderClientHeaders(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
    [token]
  );

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: COMMANDER_GO_API_PATH,
        headers: baseHeaders,
        body: { threadId, appContext },
      }),
    [baseHeaders, threadId, appContext]
  );

  const {
    messages,
    sendMessage: rawSendMessage,
    setMessages,
    regenerate: rawRegenerate,
    status,
    error,
  } = useChat({
    id: threadId,
    transport,
  });

  const isLoading = status === 'streaming' || status === 'submitted';

  // Track panel open
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      analytics.track('commander_go_opened', {
        app_context: appContext ?? 'unknown',
        has_existing_threads: threads.length > 0,
        current_thread_is_empty: messages.length === 0,
      });
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, appContext, threads.length, messages.length]);

  // Wrapped sendMessage with analytics. Accepts a plain string, a `{ text }`
  // object, or `{ text, parts }` so interactive blocks can attach structured
  // UIMessage data parts (e.g. `data-user-answer`) to the user message.
  const sendMessage = useCallback(
    (input: string | { text: string; parts?: UIMessage['parts'] } | { content: string }) => {
      let text = '';
      let parts: UIMessage['parts'] | undefined;
      if (typeof input === 'string') {
        text = input;
      } else if ('text' in input && typeof input.text === 'string') {
        text = input.text;
        parts = 'parts' in input ? input.parts : undefined;
      } else if ('content' in input && typeof input.content === 'string') {
        text = input.content;
      }
      const isNewThread = messages.length === 0;
      analytics.track('commander_go_prompt_sent', {
        app_context: appContext ?? 'unknown',
        thread_id: threadId,
        is_new_thread: isNewThread,
        prompt_length: text.length,
        message_count_before_send: messages.length,
      });
      sendStartMsRef.current = Date.now();
      isRetryRef.current = false;
      if (parts && parts.length > 0) {
        rawSendMessage({ parts } as Parameters<typeof rawSendMessage>[0]);
      } else {
        rawSendMessage({ text });
      }
    },
    [rawSendMessage, messages.length, appContext, threadId]
  );

  // Wrapped regenerate with analytics
  const regenerate = useCallback(() => {
    analytics.track('commander_go_retry_clicked', {
      app_context: appContext ?? 'unknown',
      thread_id: threadId,
      message_count: messages.length,
    });
    sendStartMsRef.current = Date.now();
    isRetryRef.current = true;
    rawRegenerate();
  }, [rawRegenerate, appContext, threadId, messages.length]);

  // Apply pending messages after useChat reinitializes with new threadId
  // biome-ignore lint/correctness/useExhaustiveDependencies: threadId is intentionally used to trigger re-apply on thread switch; setMessages is stable
  useEffect(() => {
    if (pendingMessagesRef.current !== null) {
      const msgs = pendingMessagesRef.current;
      pendingMessagesRef.current = null;
      setMessages(msgs);
    }
  }, [threadId]);

  // Fetch thread list (pass cursor to load next page)
  const fetchThreads = useCallback(
    async (cursor?: string) => {
      if (!token) return;
      try {
        const url = cursor
          ? `${COMMANDER_GO_THREADS_PATH}?cursor=${encodeURIComponent(cursor)}`
          : COMMANDER_GO_THREADS_PATH;
        const res = await fetch(url, { headers: baseHeaders() });
        if (res.ok) {
          const data = await res.json();
          setThreads((prev) => (cursor ? [...prev, ...data.threads] : data.threads));
          setNextCursor(data.nextCursor ?? null);
          return data.threads as ThreadSummary[];
        }
      } catch (err) {
        console.error('Failed to fetch threads:', err);
      }
      return [];
    },
    [token, baseHeaders]
  );

  // Load a specific thread's messages
  const loadThread = useCallback(
    async (id: string) => {
      if (!token) return;
      try {
        const res = await fetch(`${COMMANDER_GO_THREADS_PATH}/${id}/messages`, {
          headers: baseHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          const uiMessages = toUIMessages(data.messages);
          setPreviousCursor(data.previousCursor ?? null);

          // Track thread loaded
          const threadMeta = threads.find((t) => t.id === id);
          const ageDays = threadMeta
            ? Math.floor(
                (Date.now() - new Date(threadMeta.created_at).getTime()) / (1000 * 60 * 60 * 24)
              )
            : 0;
          analytics.track('commander_go_thread_loaded', {
            app_context: appContext ?? 'unknown',
            thread_id: id,
            thread_age_days: ageDays,
            thread_has_title: !!threadMeta?.title,
          });

          if (id === threadId) {
            // Same thread — just set messages directly
            setMessages(uiMessages);
          } else {
            // Different thread — store messages for after useChat reinitializes
            pendingMessagesRef.current = uiMessages;
            setThreadId(id);
          }
          setShowThreadList(false);
        }
      } catch (err) {
        console.error('Failed to load thread:', err);
      }
    },
    [token, baseHeaders, setMessages, threadId, threads, appContext]
  );

  // Load older messages (cursor-based pagination)
  const loadOlderMessages = useCallback(async () => {
    if (!token || previousCursor == null || isLoadingOlder) return;
    setIsLoadingOlder(true);
    try {
      const res = await fetch(
        `${COMMANDER_GO_THREADS_PATH}/${threadId}/messages?cursor=${previousCursor}`,
        { headers: baseHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setPreviousCursor(data.previousCursor ?? null);
        const olderUIMessages = toUIMessages(data.messages);
        setMessages((prev) => [...olderUIMessages, ...prev]);
      }
    } catch (err) {
      console.error('Failed to load older messages:', err);
    } finally {
      setIsLoadingOlder(false);
    }
  }, [token, previousCursor, isLoadingOlder, threadId, baseHeaders, setMessages]);

  // Load threads on first open (but don't auto-resume — show welcome screen instead)
  useEffect(() => {
    if (isOpen && !threadsLoaded && token) {
      setThreadsLoaded(true);
      fetchThreads();
    }
  }, [isOpen, threadsLoaded, token, fetchThreads]);

  // Start a new conversation
  const newChat = useCallback(() => {
    pendingMessagesRef.current = []; // Empty array = clear messages after reinitialization
    setThreadId(crypto.randomUUID());
    setShowThreadList(false);
    setPreviousCursor(null);
  }, []);

  // Delete a thread (soft delete)
  const deleteThread = useCallback(
    async (id: string) => {
      if (!token) return;
      try {
        const res = await fetch(`${COMMANDER_GO_THREADS_PATH}/${id}`, {
          method: 'DELETE',
          headers: baseHeaders(),
        });
        if (res.ok) {
          analytics.track('commander_go_thread_deleted', {
            app_context: appContext ?? 'unknown',
            thread_id: id,
            was_active_thread: id === threadId,
          });
          setThreads((prev) => prev.filter((t) => t.id !== id));
          // If we deleted the active thread, start a new chat
          if (id === threadId) {
            newChat();
          }
        }
      } catch (err) {
        console.error('Failed to delete thread:', err);
      }
    },
    [token, baseHeaders, threadId, newChat, appContext]
  );

  // Rename a thread
  const renameThread = useCallback(
    async (id: string, title: string) => {
      if (!token) return;
      try {
        const res = await fetch(`${COMMANDER_GO_THREADS_PATH}/${id}`, {
          method: 'PATCH',
          headers: { ...baseHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ title }),
        });
        if (res.ok) {
          const data = await res.json();
          setThreads((prev) => prev.map((t) => (t.id === id ? { ...t, title: data.title } : t)));
        }
      } catch (err) {
        console.error('Failed to rename thread:', err);
      }
    },
    [token, baseHeaders]
  );

  // Toggle pin/unpin a thread
  const togglePinThread = useCallback(
    async (id: string) => {
      if (!token) return;
      try {
        const res = await fetch(`${COMMANDER_GO_THREADS_PATH}/${id}/pin`, {
          method: 'PATCH',
          headers: baseHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setThreads((prev) =>
            prev.map((t) => (t.id === id ? { ...t, pinned_at: data.pinned_at } : t))
          );
        }
      } catch (err) {
        console.error('Failed to toggle pin thread:', err);
      }
    },
    [token, baseHeaders]
  );

  // Clear all threads (soft delete)
  const clearAllThreads = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(COMMANDER_GO_THREADS_PATH, {
        method: 'DELETE',
        headers: baseHeaders(),
      });
      if (res.ok) {
        analytics.track('commander_go_threads_cleared', {
          app_context: appContext ?? 'unknown',
          thread_count: threads.length,
        });
        setThreads([]);
        setNextCursor(null);
        newChat();
      }
    } catch (err) {
      console.error('Failed to clear all threads:', err);
    }
  }, [token, baseHeaders, appContext, threads.length, newChat]);

  // Toggle thread list view
  const toggleThreadList = useCallback(() => {
    if (!showThreadList) {
      // Refresh threads when opening the list
      fetchThreads();
    }
    setShowThreadList((prev) => !prev);
  }, [showThreadList, fetchThreads]);

  // Poll for thread title after first message exchange
  const pollForTitle = useCallback(
    async (_tid: string) => {
      // Wait a few seconds for title generation, then fetch
      await new Promise((r) => setTimeout(r, 3000));
      if (!token) return;
      try {
        const res = await fetch(COMMANDER_GO_THREADS_PATH, {
          headers: baseHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setThreads(data.threads);
        }
      } catch {
        // Ignore — title will show on next thread list open
      }
    },
    [token, baseHeaders]
  );

  // Track when a new thread gets its first response — trigger title poll + analytics
  const prevStatusRef = useRef(status);

  useEffect(() => {
    const wasLoading =
      prevStatusRef.current === 'streaming' || prevStatusRef.current === 'submitted';
    const isNowReady = status === 'ready';
    const isNowError = status === 'error';
    prevStatusRef.current = status;

    if (wasLoading && isNowReady) {
      // Track response completed
      if (sendStartMsRef.current) {
        const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
        const responseText = lastAssistant
          ? lastAssistant.parts
              .filter((p) => p.type === 'text')
              .map((p) => (p.type === 'text' ? p.text : ''))
              .join('')
          : '';
        const toolParts = lastAssistant
          ? lastAssistant.parts.filter((p) => p.type === 'tool-invocation' || p.type === 'file')
          : [];
        const toolTypes = [
          ...new Set(toolParts.map((p) => ('toolName' in p ? (p as any).toolName : p.type))),
        ];
        analytics.track('commander_go_response_completed', {
          app_context: appContext ?? 'unknown',
          thread_id: threadId,
          duration_ms: Date.now() - sendStartMsRef.current,
          response_chars: responseText.length,
          had_tool_output: toolParts.length > 0,
          tool_types: toolTypes,
          is_retry: isRetryRef.current,
        });
        sendStartMsRef.current = null;
      }

      // Poll for title
      if (!hasPolledTitleRef.current.has(threadId)) {
        const thread = threads.find((t) => t.id === threadId);
        if (!thread || !thread.title) {
          hasPolledTitleRef.current.add(threadId);
          pollForTitle(threadId);
        }
      }
    }

    if (wasLoading && isNowError) {
      // Track response failed
      if (sendStartMsRef.current) {
        const hasPartial = messages.some(
          (m) => m.role === 'assistant' && m.parts.some((p) => p.type === 'text' && p.text)
        );
        analytics.track('commander_go_response_failed', {
          app_context: appContext ?? 'unknown',
          thread_id: threadId,
          error_type: error?.name,
          had_partial_response: hasPartial,
          is_retry: isRetryRef.current,
        });
        sendStartMsRef.current = null;
      }
    }
  }, [status, threadId, threads, pollForTitle, messages, appContext, error]);

  // Hide Chatwoot bubble when Commander Go panel is open
  useEffect(() => {
    const bubble = document.querySelector('.woot-widget-bubble') as HTMLElement | null;
    const closeBtn = document.querySelector('.woot--close') as HTMLElement | null;
    const holder = document.querySelector('.woot-widget-holder') as HTMLElement | null;
    for (const el of [bubble, closeBtn, holder]) {
      if (el) {
        el.style.setProperty('display', isOpen ? 'none' : '', 'important');
      }
    }
  }, [isOpen]);

  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const loadMoreThreads = useCallback(() => {
    if (nextCursor) fetchThreads(nextCursor);
  }, [nextCursor, fetchThreads]);

  return {
    messages,
    sendMessage,
    regenerate,
    newChat,
    isLoading,
    status,
    error,
    isOpen,
    toggle,
    open,
    close,
    threadId,
    appContext,
    // Thread list
    threads,
    showThreadList,
    toggleThreadList,
    loadThread,
    deleteThread,
    renameThread,
    togglePinThread,
    clearAllThreads,
    hasMoreThreads: !!nextCursor,
    loadMoreThreads,
    // Message pagination
    loadOlderMessages,
    hasPreviousMessages: previousCursor != null,
    isLoadingOlder,
  };
}
